// ===== 1. AI Inspection Type Recommendation Service =====
// src/services/aiInspectionRecommendationService.ts

import { httpRequest } from "../httpRequest";

export interface InspectionTypeRecommendation {
  inspectionType: string;
  confidence: number;
  reason: string;
  priority: 'High' | 'Medium' | 'Low';
  phase: 'Early' | 'Mid' | 'Final';
}

export interface InspectionRecommendationResponse {
  subcodeType: string;
  recommendedInspections: InspectionTypeRecommendation[];
  overallConfidence: number;
  projectPhase: 'Planning' | 'Construction' | 'Near Completion' | 'Final';
  warningMessages: string[];
  infoMessages: string[];
}

export class AIInspectionRecommendationService {

  // Subcode-wise inspection types mapping
  private inspectionTypesMap = {
    "BUILDING": [
      "Certificate of Compliance", "Final Building", "Change of Use",
      "Retaining Wall", "Partial/Temp C of O", "Wall Framing",
      "Insulation", "Floor Framing", "Above Ceiling", "Pool Deck", "Final Pool"
    ],
    "ELECTRICAL": [
      "Rough Electrical", "Final Electrical", "Temporary Power",
      "Service Panel Inspection", "Electrical Grounding", "Above Ceiling Electrical"
    ],
    "FIRE PROTECTION": [
      "Fire Alarm Test", "Sprinkler System Inspection", "Fire Final",
      "Fire Pump Test", "Smoke Detector Placement", "Emergency Lighting"
    ],
    "PLUMBING": [
      "Rough Plumbing", "Final Plumbing", "Water Service Inspection",
      "Drainage and Vent Test", "Gas Piping Pressure Test", "Underground Plumbing"
    ],
    "MECHANICAL": [
      "Rough Mechanical", "Final Mechanical", "HVAC Ductwork Inspection",
      "Refrigerant Piping", "Exhaust System", "Mechanical Equipment Clearance"
    ]
  };

  async getInspectionRecommendations(
    subcodeType: string,
    applicationData: any,
    subcodeFormData: any
  ): Promise<InspectionRecommendationResponse> {
    try {
      const response1 = await httpRequest('POST', '/construction/AI/generateAIInspectionRecommendations', {
        subcodeType,
        applicationData,
        subcodeFormData
      })

      if (response1?.status !== 'success') {
        throw new Error(`API request failed: ${response1?.message}`);
      }

      const recommendations = response1?.data
      return this.validateAndSanitize(recommendations, subcodeType);

    } catch (error) {
      console.error('AI Inspection Recommendation Error:', error);
      return this.getFallbackRecommendations(subcodeType, applicationData);
    }
  }

  private validateAndSanitize(
    recommendations: any,
    subcodeType: string
  ): InspectionRecommendationResponse {
    const availableInspections = this.inspectionTypesMap[subcodeType] || [];
    if (!recommendations.recommendedInspections || !Array.isArray(recommendations.recommendedInspections)) {
      throw new Error('Invalid AI response format');
    }

    // Filter out any recommendations not in the available list
    recommendations.recommendedInspections = recommendations.recommendedInspections.filter((rec: any) => {
      return rec.inspectionType &&
        availableInspections.includes(rec.inspectionType) &&
        rec.confidence >= 0 && rec.confidence <= 1;
    });

    // Sort by confidence and priority
    recommendations.recommendedInspections.sort((a: any, b: any) => {
      const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
      const aPriority = priorityOrder[a.priority] || 1;
      const bPriority = priorityOrder[b.priority] || 1;

      if (aPriority !== bPriority) return bPriority - aPriority;
      return b.confidence - a.confidence;
    });

    return {
      subcodeType,
      recommendedInspections: recommendations.recommendedInspections,
      overallConfidence: Math.max(0, Math.min(1, recommendations.overallConfidence || 0.7)),
      projectPhase: recommendations.projectPhase || 'Construction',
      warningMessages: recommendations.warningMessages || [],
      infoMessages: recommendations.infoMessages || []
    };
  }

  private getFallbackRecommendations(
    subcodeType: string,
    applicationData: any,
    //   subcodeFormData: any
  ): InspectionRecommendationResponse {
    //   const availableInspections = this.inspectionTypesMap[subcodeType] || [];
    const purposeTypes = Array.isArray(applicationData.PurposeType)
      ? applicationData.PurposeType
      : [applicationData.PurposeType];

    // Basic logic for common inspection types
    const fallbackRecommendations: InspectionTypeRecommendation[] = [];

    // Building subcode logic
    if (subcodeType === 'Building') {
      if (purposeTypes.includes('New Building')) {
        fallbackRecommendations.push({
          inspectionType: 'Wall Framing',
          confidence: 0.9,
          reason: 'Wall framing inspection required for new construction',
          priority: 'High',
          phase: 'Early'
        });
        fallbackRecommendations.push({
          inspectionType: 'Final Building',
          confidence: 0.8,
          reason: 'Final building inspection required before occupancy',
          priority: 'High',
          phase: 'Final'
        });
      }
      if (purposeTypes.includes('Renovation/Existing Building')) {
        fallbackRecommendations.push({
          inspectionType: 'Certificate of Compliance',
          confidence: 0.8,
          reason: 'Certificate of compliance typically needed for renovations',
          priority: 'Medium',
          phase: 'Final'
        });
      }
    }

    // Electrical subcode logic
    if (subcodeType === 'Electrical') {
      fallbackRecommendations.push({
        inspectionType: 'Rough Electrical',
        confidence: 0.8,
        reason: 'Rough electrical inspection typically required',
        priority: 'High',
        phase: 'Early'
      });
      fallbackRecommendations.push({
        inspectionType: 'Final Electrical',
        confidence: 0.8,
        reason: 'Final electrical inspection required',
        priority: 'High',
        phase: 'Final'
      });
    }

    // Plumbing subcode logic
    if (subcodeType === 'Plumbing') {
      fallbackRecommendations.push({
        inspectionType: 'Rough Plumbing',
        confidence: 0.8,
        reason: 'Rough plumbing inspection typically required',
        priority: 'High',
        phase: 'Early'
      });
      fallbackRecommendations.push({
        inspectionType: 'Final Plumbing',
        confidence: 0.8,
        reason: 'Final plumbing inspection required',
        priority: 'High',
        phase: 'Final'
      });
    }

    return {
      subcodeType,
      recommendedInspections: fallbackRecommendations,
      overallConfidence: 0.7,
      projectPhase: 'Construction',
      warningMessages: ['AI analysis unavailable - using basic recommendations'],
      infoMessages: ['Contact inspector for specific requirements']
    };
  }
}